/**
 * Single source of truth for site-wide SEO facts, structured data, and route
 * classification. Used by the browser (SeoMetaTags), the SSR entry, the
 * prerender script, and the SEO audit so none of them can drift apart.
 */
export const SITE_ORIGIN = "https://www.djrequest.net";
export const SITE_NAME = "DJ Request";
export const LEGAL_NAME = "Naranja LLC";
export const SUPPORT_EMAIL = "support@djrequest.net";
export const DEFAULT_ROBOTS = "index,follow,max-image-preview:large";
export const NOINDEX_ROBOTS = "noindex,nofollow,noarchive";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/icons/icon-512.png`;
export const DEFAULT_AUTHOR = "DJ Request Editorial Team";

export const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#organization`,
  name: SITE_NAME,
  legalName: LEGAL_NAME,
  url: `${SITE_ORIGIN}/`,
  logo: DEFAULT_OG_IMAGE,
  sameAs: ["https://instagram.com/dj_request_app"],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: SUPPORT_EMAIL,
    url: `${SITE_ORIGIN}/support`,
    availableLanguage: ["English"],
  },
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_ORIGIN}/#website`,
  name: SITE_NAME,
  url: `${SITE_ORIGIN}/`,
  publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  inLanguage: "en-US",
};

export function normalizeCanonicalPath(pathname = "/") {
  let normalizedPath = String(pathname || "/").replace(/\/{2,}/g, "/");
  if (!normalizedPath.startsWith("/")) normalizedPath = `/${normalizedPath}`;
  if (normalizedPath.length > 1) normalizedPath = normalizedPath.replace(/\/+$/, "");
  return normalizedPath || "/";
}

export function toAbsoluteUrl(pathname = "/") {
  return `${SITE_ORIGIN}${normalizeCanonicalPath(pathname)}`;
}

export function buildFaqSchema(faqs = []) {
  if (!Array.isArray(faqs) || !faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

export function buildWebPageSchema({ title, description, canonicalUrl, lastModified }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${canonicalUrl}#webpage`,
    name: title,
    url: canonicalUrl,
    description,
    inLanguage: "en-US",
    ...(lastModified ? { dateModified: lastModified } : {}),
    author: { "@type": "Organization", name: SITE_NAME },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      legalName: LEGAL_NAME,
      logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE },
    },
    mainEntityOfPage: { "@id": canonicalUrl },
    isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
    about: { "@id": `${SITE_ORIGIN}/#organization` },
  };
}

export function buildBreadcrumbSchema({ title, canonicalUrl, routePath }) {
  if (normalizeCanonicalPath(routePath) === "/") return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: String(title || "Page").replace(/\s+\|\s+DJ Request.*$/i, "").trim() || "Page",
        item: canonicalUrl,
      },
    ],
  };
}

/**
 * Build the complete JSON-LD set for a page from its SeoMetaTags props.
 * Page-provided items win over generated ones when the same @type repeats, so
 * the homepage WebApplication schema and page FAQPage schemas stay unique.
 */
export function buildStructuredData({
  title,
  description,
  canonicalPath,
  routePath,
  lastModified,
  structuredData,
}) {
  const canonicalUrl = toAbsoluteUrl(canonicalPath || routePath || "/");
  const provided = (Array.isArray(structuredData) ? structuredData : [structuredData]).filter(
    (item) => item && typeof item === "object"
  );
  const generated = [
    ORGANIZATION_SCHEMA,
    WEBSITE_SCHEMA,
    title && description ? buildWebPageSchema({ title, description, canonicalUrl, lastModified }) : null,
    buildBreadcrumbSchema({ title, canonicalUrl, routePath: routePath || canonicalPath || "/" }),
  ].filter(Boolean);

  const seenTypes = new Set();
  const output = [];
  for (const item of [...provided, ...generated]) {
    const type = String(item["@type"] || "");
    if (type && seenTypes.has(type)) continue;
    if (type) seenTypes.add(type);
    output.push(item);
  }
  return output;
}

/** Route prefixes that must never be prerendered with private/account data. */
export const PRIVATE_ROUTE_PREFIXES = [
  "/dj",
  "/join",
  "/wall",
  "/album",
  "/signin",
  "/return",
  "/feedback",
  "/qr-poster",
];

/** Fixed routes that stay crawlable but are never indexed. */
export const FORCE_NOINDEX_ROUTES = new Map([
  ["/support", "Support | DJ Request"],
  ["/signin", "Sign In | DJ Request"],
  ["/return", "Return | DJ Request"],
  ["/feedback", "Feedback | DJ Request"],
  ["/dj", "DJ Dashboard | DJ Request"],
  ["/qr-poster", "QR Poster | DJ Request"],
]);

/** Legacy URLs that 301 at the Hosting layer (mirrored in firebase.json). */
export const DEPRECATED_ROUTE_REDIRECTS = new Map([
  ["/mobile-dj-app", "/dj-request-app-for-mobile-djs"],
  ["/wedding-dj-software", "/wedding-dj-song-requests"],
  ["/dj-request-app-qr-code", "/qr-code-song-requests"],
  ["/dj-request-qr-code", "/qr-code-song-requests"],
  ["/dj-request-app-for-karaoke", "/karaoke-dj-app"],
  ["/karaoke-dj-software", "/karaoke-dj-app"],
  ["/karaoke-queue-management", "/karaoke-dj-app"],
]);

/** Pages that own the main commercial searches. */
export const PRIORITY_ROUTES = [
  "/",
  "/dj-request-app",
  "/qr-code-song-requests",
  "/get-paid-song-requests",
  "/wedding-dj-song-requests",
  "/karaoke-dj-app",
  "/event-photo-wall",
  "/pricing",
];

export function isPrivateRoutePath(pathname = "/") {
  const normalized = normalizeCanonicalPath(pathname);
  return PRIVATE_ROUTE_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  );
}
